import { useEffect } from 'react';
import { Box } from '@chakra-ui/react';
import { Controller, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PlusIcon } from 'Asset/icon';
import { Iteration } from 'Component/others';
import useGetBrand from 'Domain/brand/useGetBrand';
import { TextBold, TextSemiBold } from 'UiKit/typography';
import ButtonOutline from 'UiKit/button/buttonOutline';
import InputSelect from 'UiKit/input-select';
import InputComponent from 'UiKit/input';
import useGetGiveawayType from 'Domain/brief-live/useGetGiveawayType';
import usePostUserImage from 'Domain/brief-live/usePostUserImage';

import GiveawayItem from 'View/brief-live/component/giveaway-item';
import { useBriefGiveawayFormState } from 'View/brief-live/state/giveaway-form';

import { InitialGiveawayItem, GIVEAWAY_FORM_DRAFT_KEY } from '../const';

import styles from './styles.module.css';

const InsertGiveaway = ({ form }) => {
  const { t } = useTranslation('desktop-brief-live');
  const {
    control,
    register,
    setValue,
    watch,
    reset,
    getValues,
    formState: { errors },
  } = form;
  const { data: optionsBrand } = useGetBrand();
  const { data: optionsGiveawayType } = useGetGiveawayType();
  const { mutateAsync: uploadImage } = usePostUserImage();
  const { updateFilterState } = useBriefGiveawayFormState();

  const watchedGiveaways = watch('giveaways') || [];
  const watchedBrand = watch('brandId') || null;

  const { fields: giveaways, append, remove } = useFieldArray({ control, name: 'giveaways' });

  useEffect(() => {
    const draft = sessionStorage.getItem(GIVEAWAY_FORM_DRAFT_KEY);
    if (draft) {
      try {
        const parsed = JSON.parse(draft);
        reset(parsed);
        if (parsed.brandId) updateFilterState({ brand: parsed.brandId });
      } catch {
        sessionStorage.removeItem(GIVEAWAY_FORM_DRAFT_KEY);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBlur = () => {
    sessionStorage.setItem(GIVEAWAY_FORM_DRAFT_KEY, JSON.stringify(getValues()));
  };

  const handleBrandChange = (onChange) => (value) => {
    onChange(value);
    if (value?.value === watchedBrand?.value) return;
    const currentGiveaways = getValues('giveaways') || [];
    updateFilterState({ brand: value });
    currentGiveaways.forEach((_, index) => {
      setValue(`giveaways.${index}.rules.requiredProducts`, []);
    });
  };

  return (
    <Box className={styles['form__container']} onBlur={handleBlur}>
      <Box className={styles['form__box--giveaway-name']}>
        <Box className={styles['form__box--input-brand']}>
          <Controller
            name="brandId"
            control={control}
            rules={{
              required: 'Brand is required',
              validate: (value) => value !== null || 'Please select a brand',
            }}
            render={({ field }) => (
              <InputSelect
                label={t('formGiveaway.brand')}
                placeholder={t('formGiveaway.brand')}
                options={optionsBrand || []}
                value={field.value}
                onChange={handleBrandChange(field.onChange)}
                isRequired
                error={errors.brandId}
              />
            )}
          />
        </Box>
        <Box className={styles['form__box--input-name']}>
          <InputComponent
            label={t('formGiveaway.giveawayName')}
            placeholder={t('formGiveaway.giveawayName')}
            value={watch('title')}
            register={register('title')}
            error={errors.title}
          />
        </Box>
      </Box>
      <Box className={styles['form__box--gift-requirement']}>
        <TextBold className={styles['form__text--gift-requirement-title']}>{t('formGiveaway.prizeAndTerms')}</TextBold>
        <ButtonOutline
          height={'34px'}
          leftIcon={<PlusIcon width="14px" height="14px" color="var(--color-primary70)" />}
          onClick={() => append(InitialGiveawayItem)}
        >
          <TextSemiBold className={styles['form__text--btn-add-gift']}>{t('formGiveaway.newPrizeAndTerms')}</TextSemiBold>
        </ButtonOutline>
      </Box>
      <Iteration
        items={giveaways}
        render={(_, index) => (
          <GiveawayItem
            key={index}
            t={t}
            index={index}
            control={control}
            register={register}
            setValue={setValue}
            errors={errors}
            watchedGiveaways={watchedGiveaways}
            watchedBrand={watchedBrand}
            optionsGiveawayType={optionsGiveawayType}
            uploadImage={uploadImage}
            remove={remove}
            onUploadSuccess={handleBlur}
          />
        )}
      />
    </Box>
  );
};

export default InsertGiveaway;
